import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useHasActiveAccess, SubscriberCtaContent } from '../components/AdaptiveCta';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    Box,
    Button,
    Container,
    Typography,
    Grid,
    Fab,
    Chip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import TimerIcon from '@mui/icons-material/Timer';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TranslateIcon from '@mui/icons-material/Translate';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { keyframes } from '@mui/system';

const cyan = '#00BFFF';
const green = '#8DC63F';
const navy = '#1B2A4A';
const cardBg = '#141E30';
const borderColor = 'rgba(0, 191, 255, 0.12)';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const GlassCard = ({ children, sx = {}, ...props }) => (
    <Box
        sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha(cardBg, 0.7) : 'background.paper',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            backdropFilter: 'blur(12px)',
            p: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
                borderColor: alpha(cyan, 0.3),
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 40px ${alpha('#000', 0.4)}`,
            },
            ...sx,
        }}
        {...props}
    >
        {children}
    </Box>
);

// SVG progress ring component
const ProgressRing = ({ value, size = 80, stroke = 6, color = cyan, label }) => {
    const radius = (size - stroke) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (value / 100) * circumference;
    return (
        <Box sx={{ position: 'relative', display: 'inline-flex', width: size, height: size }}>
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={alpha(color, 0.15)} strokeWidth={stroke} />
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
                    strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 1s ease' }} />
            </svg>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9rem' }}>{value}%</Typography>
                {label && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>{label}</Typography>}
            </Box>
        </Box>
    );
};

const ScrollToTopButton = () => {
    const [visible, setVisible] = React.useState(false);
    const [fade, setFade] = React.useState(false);

    React.useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 200) {
                setVisible(true);
            } else {
                setVisible(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleClick = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setFade(true);
        setTimeout(() => {
            setVisible(false);
            setFade(false);
        }, 500);
    };

    if (!visible) return null;

    return (
        <Box
            onClick={handleClick}
            sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 1000,
                opacity: fade ? 0 : 1,
                transition: 'opacity 0.5s ease-out',
            }}
        >
            <Fab color="primary" aria-label="scroll back to top">
                <KeyboardArrowUpIcon />
            </Fab>
        </Box>
    );
};

const CitationDialog = ({ open, onClose }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
            sx: {
                borderRadius: 3,
                backgroundColor: 'background.paper',
                border: (theme) => `1px solid ${theme.palette.divider}`,
            },
        }}
    >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Source: Average Reading Speed</Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                The average adult reading speed of 238 words per minute comes from a large-scale meta-analysis of 190 studies covering 18,573 participants.
            </Typography>
            <Box
                sx={{
                    p: 2.5,
                    borderRadius: 2,
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,191,255,0.06)' : 'rgba(0,0,0,0.03)',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    mb: 2,
                }}
            >
                <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.8, color: 'text.secondary' }}>
                    Brysbaert, M. (2019). How many words do we read per minute? A review and meta-analysis of reading rate.
                    {' '}<em>Journal of Memory and Language</em>, 109, 104047.
                </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                Key findings from the study:
            </Typography>
            <Box component="ul" sx={{ color: 'text.secondary', pl: 3, '& li': { mb: 1, lineHeight: 1.7 } }}>
                <li>Silent reading for adults averages 238 words per minute.</li>
                <li>Reading aloud averages 183 words per minute.</li>
                <li>The range for typical adults is roughly 175 to 300 WPM for silent reading.</li>
                <li>Speed varies based on text difficulty, language, and individual reading experience.</li>
            </Box>
            <Box sx={{ mt: 2 }}>
                <Link
                    href="https://doi.org/10.1016/j.jml.2019.104047"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    sx={{ fontSize: '0.875rem' }}
                >
                    Read the full study (doi.org)
                </Link>
            </Box>
        </DialogContent>
    </Dialog>
);

const Home = () => {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const hasActiveAccess = useHasActiveAccess();
    const [citationOpen, setCitationOpen] = React.useState(false);

    React.useEffect(() => {
        if (isAuthenticated) {
            navigate('/dashboard');
            return;
        }
    }, [isAuthenticated, navigate]);

    if (isAuthenticated) {
        return null;
    }

    const features = [
        { icon: <SpeedIcon sx={{ fontSize: 36 }} />, title: 'Adaptive Training', desc: 'Exercises that adjust to your progress and learning style' },
        { icon: <TrendingUpIcon sx={{ fontSize: 36 }} />, title: 'Progress Analytics', desc: 'Detailed metrics, charts, and weekly trend tracking' },
        { icon: <EmojiEventsIcon sx={{ fontSize: 36 }} />, title: '104 Badges', desc: 'Across 16 categories with 5 rarity tiers to unlock' },
        { icon: <SchoolIcon sx={{ fontSize: 36 }} />, title: 'AI-Powered Articles', desc: 'Content matched to your interests and reading level' },
        { icon: <TranslateIcon sx={{ fontSize: 36 }} />, title: '16 Languages', desc: 'Read and train in English, Spanish, Japanese, and more' },
        { icon: <PsychologyIcon sx={{ fontSize: 36 }} />, title: 'Comprehension Quizzes', desc: 'Verify retention after every reading session' },
        { icon: <MenuBookIcon sx={{ fontSize: 36 }} />, title: 'Vocabulary Bank', desc: 'Save words with spaced-repetition flashcards' },
        { icon: <TimerIcon sx={{ fontSize: 36 }} />, title: 'FLEX Cards', desc: 'Share reading achievements and challenge friends to head-to-head duels' },
    ];

    return (
        <Box sx={{ overflow: 'hidden' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    position: 'relative',
                    minHeight: '90vh',
                    display: 'flex',
                    alignItems: 'center',
                    pt: { xs: 8, md: 0 },
                    pb: { xs: 8, md: 0 },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: `radial-gradient(ellipse at 30% 20%, ${alpha(cyan, 0.08)} 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, ${alpha(green, 0.06)} 0%, transparent 60%)`,
                        pointerEvents: 'none',
                    },
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{ animation: `${fadeIn} 0.8s ease-out` }}>
                                <Chip
                                    label="$1 for 3 days - full access"
                                    sx={{
                                        mb: 3,
                                        backgroundColor: alpha(green, 0.15),
                                        color: green,
                                        fontWeight: 600,
                                        border: `1px solid ${alpha(green, 0.3)}`,
                                    }}
                                />
                                <Typography
                                    component="h1"
                                    variant="h1"
                                    sx={{
                                        fontWeight: 800,
                                        mb: 3,
                                        fontSize: { xs: '2.5rem', md: '3.8rem' },
                                        lineHeight: 1.15,
                                        color: 'text.primary',
                                    }}
                                >
                                    Read Faster.<br />
                                    <Box component="span" sx={{ color: cyan }}>Learn More.</Box><br />
                                    Save Time.
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{ mb: 4, color: 'text.secondary', fontWeight: 400, lineHeight: 1.6, maxWidth: '90%' }}
                                >
                                    Triple your reading speed while keeping comprehension high. AI-powered training in 16 languages with progress tracking, quizzes, and gamified achievements.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {hasActiveAccess ? (
                                        <>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                size="large"
                                                onClick={() => {
                                                    const link = `${window.location.origin}?ref=${user?.username || ''}`;
                                                    navigator.clipboard.writeText(link);
                                                }}
                                                startIcon={<ContentCopyIcon />}
                                                sx={{ py: 1.5, px: 5, fontSize: '1.05rem' }}
                                            >
                                                Copy Affiliate Link
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                size="large"
                                                onClick={() => navigate('/affiliate')}
                                                sx={{ py: 1.5, px: 5 }}
                                            >
                                                Affiliate Dashboard
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                size="large"
                                                onClick={() => navigate('/register')}
                                                sx={{ py: 1.5, px: 5, fontSize: '1.05rem' }}
                                            >
                                                Start Your Trial
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                size="large"
                                                onClick={() => navigate('/login')}
                                                sx={{ py: 1.5, px: 5 }}
                                            >
                                                Login
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Grid>

                        {/* Demo Reading Session Card */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ animation: `${fadeIn} 0.8s ease-out 0.3s both` }}>
                                <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
                                    {/* Card header */}
                                    <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>Reading Session</Typography>
                                            <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>The Future of Renewable Energy</Typography>
                                        </Box>
                                        <Chip label="Grade 10" size="small" sx={{ backgroundColor: alpha(cyan, 0.15), color: cyan, fontWeight: 600 }} />
                                    </Box>
                                    {/* Stats row */}
                                    <Box sx={{ px: 3, py: 2.5, display: 'flex', justifyContent: 'space-around', borderBottom: `1px solid ${borderColor}` }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h4" sx={{ color: cyan, fontWeight: 700 }}>487</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>WPM</Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <ProgressRing value={92} size={64} color={green} label="COMP" />
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h4" sx={{ color: green, fontWeight: 700 }}>+35</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>XP Earned</Typography>
                                        </Box>
                                    </Box>
                                    {/* Progress bar */}
                                    <Box sx={{ px: 3, py: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Article Progress</Typography>
                                            <Typography variant="caption" sx={{ color: cyan }}>78%</Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={78} sx={{ height: 6, borderRadius: 3, backgroundColor: alpha(cyan, 0.1), '& .MuiLinearProgress-bar': { backgroundColor: cyan, borderRadius: 3 } }} />
                                    </Box>
                                    {/* Badge preview row */}
                                    <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>Recent badges:</Typography>
                                        {['Speed Demon', 'Word Collector', 'Bilingual'].map((badge) => (
                                            <Chip
                                                key={badge}
                                                label={badge}
                                                size="small"
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    height: 24,
                                                    backgroundColor: alpha(cyan, 0.1),
                                                    color: 'text.primary',
                                                    border: `1px solid ${alpha(cyan, 0.2)}`,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </GlassCard>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Pain Point Section */}
            <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: alpha('#000', 0.25) }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" align="center" sx={{ mb: 2, color: 'text.primary', fontSize: { xs: '2rem', md: '2.75rem' } }}>
                        What Slow Reading is <Box component="span" sx={{ color: '#FF5252' }}>Costing You</Box>
                    </Typography>
                    <Typography variant="h6" align="center" sx={{ mb: 8, color: 'text.secondary', maxWidth: 650, mx: 'auto', fontWeight: 400 }}>
                        The average adult reads{' '}
                        <Box
                            component="span"
                            onClick={() => setCitationOpen(true)}
                            sx={{
                                color: cyan,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted',
                                textUnderlineOffset: '3px',
                                '&:hover': { color: '#33CCFF' },
                            }}
                        >
                            200-250 WPM
                        </Box>.
                        {' '}At that speed, you're leaving hours on the table every single week.
                    </Typography>
                    <Grid container spacing={3}>
                        {[
                            { stat: '2-3 hrs', title: 'Wasted Daily', desc: 'The time slow readers lose to emails, reports, and articles that faster readers finish in half the time.', color: '#FF5252' },
                            { stat: '47%', title: 'Lower Retention', desc: 'Untrained readers retain less because they re-read sentences and lose focus. Speed training fixes both problems.', color: '#FFB74D' },
                            { stat: '$$$', title: 'Career Impact', desc: 'Professionals who read faster process more information, make quicker decisions, and stay ahead of their peers.', color: '#FF5252' },
                            { stat: '84%', title: 'Feel Overwhelmed', desc: 'Most knowledge workers say they cannot keep up with the volume of reading required for their job.', color: '#FFB74D' },
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <GlassCard sx={{ height: '100%', textAlign: 'center', borderColor: alpha(item.color, 0.15) }}>
                                    <Typography variant="h3" sx={{ fontWeight: 800, color: item.color, mb: 1 }}>{item.stat}</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>{item.title}</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{item.desc}</Typography>
                                </GlassCard>
                            </Grid>
                        ))}
                    </Grid>
                    <Box sx={{ textAlign: 'center', mt: 6 }}>
                        <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                            The good news? Reading speed is a <Box component={RouterLink} to="/blog/reading-speed-is-a-trainable-skill" sx={{ color: green, textDecoration: 'underline', textUnderlineOffset: '3px', '&:hover': { color: '#A4D65E' } }}>trainable skill</Box>.
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 500, mx: 'auto' }}>
                            ReaderFlex users typically double their speed within 30 days while improving comprehension at the same time.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* Stats Section */}
            <Box sx={{ py: { xs: 6, md: 10 }, borderTop: `1px solid ${borderColor}`, borderBottom: `1px solid ${borderColor}` }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} justifyContent="center">
                        {[
                            { value: '3x', label: 'Average Speed Increase', color: cyan },
                            { value: '90%', label: 'Comprehension Maintained', color: green },
                            { value: '16', label: 'Languages Supported', color: cyan },
                            { value: '104', label: 'Badges to Unlock', color: green },
                        ].map((stat, i) => (
                            <Grid item xs={6} md={3} key={i}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h2" sx={{ fontWeight: 800, color: stat.color, mb: 0.5 }}>{stat.value}</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{stat.label}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Benefits Section */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                <Typography variant="h2" align="center" sx={{ mb: 1.5, color: 'text.primary', fontSize: { xs: '2rem', md: '2.75rem' } }}>
                    Your Reading <Box component="span" sx={{ color: cyan }}>Performance Dashboard</Box>
                </Typography>
                <Typography variant="h6" align="center" sx={{ mb: 8, color: 'text.secondary', maxWidth: 650, mx: 'auto', fontWeight: 400 }}>
                    Track every metric, unlock achievements, and watch your reading ability grow week over week.
                </Typography>
                <Grid container spacing={3}>
                    {[
                        { icon: <TimerIcon sx={{ fontSize: 36 }} />, title: 'Save 2+ Hours Daily', desc: 'Finish books in half the time while retaining more information.', color: cyan },
                        { icon: <MenuBookIcon sx={{ fontSize: 36 }} />, title: 'Read More, Learn More', desc: 'Double your reading speed within 30 days of consistent practice.', color: green },
                        { icon: <PsychologyIcon sx={{ fontSize: 36 }} />, title: 'Boost Comprehension', desc: 'Our techniques improve both speed and understanding at the same time.', color: cyan },
                    ].map((b, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <GlassCard sx={{ height: '100%' }}>
                                <Box sx={{ width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(b.color, 0.12), color: b.color, mb: 2 }}>
                                    {b.icon}
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>{b.title}</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{b.desc}</Typography>
                            </GlassCard>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Features Grid */}
            <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: alpha('#000', 0.2) }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" align="center" sx={{ mb: 1.5, color: 'text.primary', fontSize: { xs: '2rem', md: '2.75rem' } }}>
                        Everything You Need to <Box component="span" sx={{ color: green }}>Level Up</Box>
                    </Typography>
                    <Typography variant="h6" align="center" sx={{ mb: 8, color: 'text.secondary', maxWidth: 650, mx: 'auto', fontWeight: 400 }}>
                        Built for serious readers who want measurable results.
                    </Typography>
                    <Grid container spacing={3}>
                        {features.map((f, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <GlassCard sx={{ height: '100%', textAlign: 'center' }}>
                                    <Box sx={{ color: i % 2 === 0 ? cyan : green, mb: 1.5 }}>{f.icon}</Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>{f.title}</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{f.desc}</Typography>
                                </GlassCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Final CTA */}
            <Box
                sx={{
                    py: { xs: 8, md: 12 },
                    position: 'relative',
                    textAlign: 'center',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: '50%', transform: 'translateX(-50%)',
                        width: '80%', height: '1px',
                        background: `linear-gradient(90deg, transparent, ${alpha(cyan, 0.3)}, transparent)`,
                    },
                }}
            >
                <Container maxWidth="md">
                    {hasActiveAccess ? (
                        <SubscriberCtaContent
                            title="Ready to Read 3x Faster?"
                            description="Share your affiliate link and earn $3.00/month when friends start their own reading journey."
                        />
                    ) : (
                        <>
                            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, mb: 2, color: 'text.primary', fontSize: { xs: '2rem', md: '2.75rem' } }}>
                                Ready to Read <Box component="span" sx={{ color: green }}>3x Faster</Box>?
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 5, color: 'text.secondary', fontWeight: 400, maxWidth: 600, mx: 'auto' }}>
                                Try it for $1. Full access for 3 days. No auto-billing, cancel anytime.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                    onClick={() => navigate('/register')}
                                    sx={{ py: 1.5, px: 6, fontSize: '1.1rem' }}
                                >
                                    Start Your Trial
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    onClick={() => navigate('/subscription')}
                                    sx={{ py: 1.5, px: 6 }}
                                >
                                    View Pricing
                                </Button>
                            </Box>
                            <Box sx={{ mt: 3, animation: `${pulse} 3s ease-in-out infinite` }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    No credit card surprises. $1 trial, then choose your plan.
                                </Typography>
                            </Box>
                        </>
                    )}
                </Container>
            </Box>
            <CitationDialog open={citationOpen} onClose={() => setCitationOpen(false)} />
            <ScrollToTopButton />
        </Box>
    );
};

export default Home;
